import { Injectable } from '@angular/core';
import { Account } from '@mitel/cloudlink-sdk';
import { AppInjector } from '../../app-injector';
import { AccountsService } from './accounts.service';
import { AppUserClaimsService } from './app-user-claims.service';
import { SpinnerService } from './spinner.service';
import { AdminService } from './admin.service';
import { Statement } from '@mitel/cloudlink-sdk/admin';
import { IntegrationChangeType, IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { Util } from '../utils/Util';
import { AuthService } from './auth.service';
import { PolicyEffects } from '../constants/constants';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { StatusType } from '../constants/statusType.enum';
import { MiVBAccountSettingsService } from './mivb-account-settings.service';
import { CAPFeaturePolicyTestRequest, EnableCAPAccountAccessStatement } from '../constants/productPolicies';
@Injectable({
	providedIn: 'root'
})
export class IntegCustomerAdminPortalService implements IntegrationItemServiceInf {

	private accountsService: AccountsService = undefined;
	private adminService: AdminService = undefined;
	private authService: AuthService = undefined;
	private appClaimsService: AppUserClaimsService = undefined;
	private spinnerService: SpinnerService = undefined;
	private policyService: IntegPolicyService = undefined;
	private mivbAccountSettingsService: MiVBAccountSettingsService = undefined;

	constructor() { }

	initServices(): Promise<any> {

		if (!this.accountsService)
			this.accountsService = AppInjector.injector.get(AccountsService);

		if (!this.adminService)
			this.adminService = AppInjector.injector.get(AdminService);

		if (!this.authService)
			this.authService = AppInjector.injector.get(AuthService);

		if (!this.appClaimsService)
			this.appClaimsService = AppInjector.injector.get(AppUserClaimsService);

		if (!this.spinnerService)
			this.spinnerService = AppInjector.injector.get(SpinnerService);
		
		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.mivbAccountSettingsService )
			this.mivbAccountSettingsService = AppInjector.injector.get(MiVBAccountSettingsService);

		return Promise.resolve();
	}

	isIntegrationStateChangeable(account: Account): Promise<boolean>
	{
		this.initServices();
		return this.isSetupComplete( account );
	}
	
	isIntegrationStateOn(account: Account): Promise<boolean>
	{
		this.initServices();

		// check the policy here
		return this.isCAPFeatureEnabled( account );
	}

	canBeSelected(account: Account): Promise<boolean>{
		return Promise.resolve(false);
	}

	getConfigurationDialog() {
		return undefined;
	}

	//'CAP' --> Admistration toggle for MiVoice Business account integation
	//Here we determine if the requirements are met to toggle it ON.
	//If turning it on is not allowed, determine the reason
	private determineCapPermission(account: Account): Promise<any> {
		this.initServices();
		// Must have an onboarded GW with an MiVB
		return this.adminService.getAccountTags(account.accountId).then( async tags => {
			//Keeping this old logic for now - concern was rasied in CLSD-4098
			if(tags && tags.products && tags.products.includes('MiVB')){
				if ( tags.mivb && tags.mivb.setup == true ){
					let permissionData = {
						allowed: true
					};
					return permissionData;
				}
				else
				{
					return this.mivbAccountSettingsService.isMiVBInNetworkElements()
					.then((mivbInNetworkElements) => {
						//mivbInNetworkElements is true when a 3300ICP exists in the network elements (determined by mivb-micro) (should be true for both CLD and GWP method)
						//mivbInNetworkElements is false when the call to check network elements fails, or there is no 3300ICP
						//in a CLD setup, you can have a 3300ICP in network elements, but no mivb onboarded in GWP
			
						let pbxOnboardedViaGWP = null;
						let gwpIntegrationON = tags.products.includes('Gateway') ? true: false;

						//If the CloudLink Gateway integration is on --> look for pbx tags
						//Even if they are halfway through onboarding, we still care about the pbx type
						if(gwpIntegrationON){
							let pbxTypesFromTags = Util.getPbxTypesFromTags(tags);
							if(pbxTypesFromTags && pbxTypesFromTags.length === 1){
								pbxOnboardedViaGWP = pbxTypesFromTags[0];
							}
							else if(pbxTypesFromTags && pbxTypesFromTags.length > 1){
								console.log("Conflicting pbx information"); //should never happen - GWP related tags in a bad state
								let permissionData = {
									allowed: false,
									type: StatusType.error,
									reason: 'integrations.customerAdminPortal.failure-analyzing-requirements'
								};
								return permissionData;
							}
							else{
								//If past the pbx step, then it's not ok to be missing the pbx tag
								let pastPBXStep = Util.isGWPOnboardingPastStep(tags, '3');
								if(pastPBXStep && (!pbxTypesFromTags || pbxTypesFromTags.length < 1)){
									console.log("Missing pbx information"); //should never happen - GWP related tags in a bad state
									let permissionData = {
										allowed: false,
										type: StatusType.error,
										reason: 'integrations.customerAdminPortal.failure-analyzing-requirements'
									};
									return permissionData;
								}
							}
						}
			
						//It's ok to have nothing onboarded in GWP (as long as you have mivb found in network elements)
						//If you have something onboarded in GWP - then it must be a MiVoice Business
						if(mivbInNetworkElements && (!pbxOnboardedViaGWP || (pbxOnboardedViaGWP === 'MiVoice Business'))){
							// for now we will update the tags for the account if they do not exist
							// in the future this most likely will change
							// the logic here around the mivb tag is old and has a hole - what should be returned if you have the mivb tag, but not mivb.setup or mivb.setup = false?
							if (tags['mivb'] == undefined)
							{
								const tagBody = {
									mivb: {
										setup: true
									}
								} // anything else required here?
								return this.adminService.createAccountTag(account.accountId, tagBody).then(newtags => {
									console.log("The TAGS:", newtags);
									let permissionData = {
										allowed: true
									};
									return permissionData;
								})
								.catch(err => {
									console.log("Could not create the tag, this is temporary so no special handling", err);
									let permissionData = {
										allowed: false,
										type: StatusType.error,
										reason: 'integrations.customerAdminPortal.failure-analyzing-requirements'
									};
									return permissionData;
								});
							}
							else{
								//assume missing mivb.setup or mivb.setup=false should not allow CAP - the 'setup' tag needs to be revisted in general
								//there was a hole in previous logic
								let permissionData = {
									allowed: false,
									type: StatusType.error,
									reason: 'integrations.customerAdminPortal.failure-analyzing-requirements'
								};
								return permissionData;
							}
						}
						else{
							//no mivb found in network elements
							//or mivb was found in network elements, but different pbx type via GWP
							let permissionData = {
								allowed: false,
								type: StatusType.caution,
								reason: 'integrations.customerAdminPortal.no-mivb'
							};
	
							//different message if pbx detected via GWP is not mivb
							if(pbxOnboardedViaGWP && pbxOnboardedViaGWP !== 'MiVoice Business'){
								permissionData.reason = 'integrations.customerAdminPortal.not-mivb-in-gwp';
							}
							return permissionData;
						}
					})
				}
			}
			//not expecting to get here - should have MiVB account integration in tags
			let permissionData = {
				allowed: false,
				type: StatusType.error,
				reason: 'integrations.customerAdminPortal.failure-analyzing-requirements'
			};
			return permissionData;
		});
	}

	isSetupComplete(account: Account): Promise<boolean> {
		return this.determineCapPermission(account).then(response => {
			if(response && 'allowed' in response){
				if(response.allowed === true){
					return true;
				}
				return false;
			}
			return false;
		}).catch(err => {
			return false;
		});
	}

	// purpose of this method is to let the base child integration know if it should notify the parent it changed
	// We don't want that to be automatic because some of the subfeatures are benign and would not
	// change the current view of the accounts app so we do not want a refresh on every toggle
	// maybe in the future if we refactor some of the integration framework, this could be handled in the 
	// stage change method? More analysis required
	shouldNotifyParentOfChange( account: Account, changeType: IntegrationChangeType ): Promise<any>
	{
		if ( changeType == IntegrationChangeType.ENABLED_STATE || changeType == IntegrationChangeType.DISABLED_STATE )
		{
			// any change to this subfeature will cause a menu change
			return Promise.resolve({product: this, reason: "refreshMenu"});
		}
		return Promise.resolve(undefined);
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
		this.initServices();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			if ( state == false )
			{
				// remove the policy the indicates the accounts app should act as a Customer Admin Portal
				return this.removeCAPFeature( newAccount ).then(ret => {
					return this.removeDeleteDisassociatePolicy( newAccount ).then(result => {
						this.spinnerService.hideSpinner();
						this.accountsService.mergeAccountWithModel(result);
						return result;
					});
				});
			}
			else
			{
				// add the policy statement for the account - Indicates that the app works as a Customer Admin Portal
				return this.policyService.addStatementToPolicy( newAccount, EnableCAPAccountAccessStatement).then( policy => {

					let dataChanges: any = {
						policy: {
							behaviorOfIntegrationDelete: "disassociate"
						}
					};
					if ( newAccount['policyId'] == undefined ) {
							dataChanges.policyId = policy.policyId;
					}
					 return this.adminService.updateAccount( newAccount.accountId, dataChanges).then(result => {
						this.spinnerService.hideSpinner();
						this.accountsService.mergeAccountWithModel(result);
						return result;
					});

				}).catch(err => {
					console.log("Failed to add/update policy", err);
					this.spinnerService.hideSpinner();
					throw err;
				});
			}
		}).catch(err => {
			console.log("Failed to get the account", err);
			this.spinnerService.hideSpinner();
			throw err;
		});
	}

	// When the parent is removed, this is the method called so the child can clean up as well
	// remove the existing mivb tags and the CAP enabled policy
	removeAsASubintegration?( account: Account ): Promise<any>
	{
		this.initServices();
		return this.adminService.getAccountTags(account.accountId).then( async tags => {
			if ( tags && tags['mivb'] != undefined )
			{
				// if the tags existed - remove them
				try
				{
					await this.adminService.deleteAccountTag(account.accountId, "mivb");
				}
				catch (err)
				{
					// delete is best effort
					// log and continue
					console.log("Error deleting mivb tags for an account ", account.accountId, tags);
				}
			}
			try
			{
				await this.removeCAPFeature( account );
			}
			catch (err2)
			{
				// delete is best effort
				// log and continue
				console.log("Error deleting mivb CAP policy ", account.accountId);
			}
			try
			{
				await this.removeDeleteDisassociatePolicy( account );
			}
			catch (err2)
			{
				// delete is best effort
				// log and continue
				console.log("Error deleting the Delete Disassociate policy ", account.accountId);
			}
			return;
		})
		.catch(err => {
			// delete is best effort
			// log and continue
			console.log("Error deleting the Customer Admin portal integration for an account ", account.accountId );
			return;
		})
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		return Promise.resolve(false); // should never be called, but just in case
	}

	getStatusString(account: Account): Promise<StatusString> {
		this.initServices();
		let status: StatusString = {
			status: undefined, 
			type: StatusType.error
		};
		return this.determineCapPermission( account ).then( response => {
			if(response && ('allowed' in response)){
				if(response.allowed === true){
					return undefined;
				}
				if('reason' in response){
					status.status = response.reason;
				}
				else{
					status.status = 'integrations.customerAdminPortal.failure-analyzing-requirements'
				}
				if('type' in response){
					status.type = response.type;
				}
			}
			else{
				status.status = 'integrations.customerAdminPortal.failure-analyzing-requirements'
			}
			return status;
		})
		.catch( err => {
			console.log("Could not retrieve setup status for the account, assume not", err);
			status.status = 'integrations.customerAdminPortal.failure-analyzing-requirements';
			return status;
		});
	}

	getConfigButtonLabel(account: Account): Promise<string> {
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean> {
		return Promise.resolve(false);
	}

	/**
	 * To program a user on the MiVB, you must have the MiVB integration AND Administration toggled on.
	 * See the Account Information page in the UX.
	 * Will check CloudLink Auth service to see if account has this feature policy enabled:
	 * 		enable-customer-admin-portal-account-access
	 * @param account 
	 * @returns true if MiVB integration AND Administration are enabled.
	 */
	//account is not required here - just quickly changing to optional 
	//ticket raised for code cleanup CLSD-4100
	isCAPFeatureEnabled( account?: Account ): Promise<boolean>
	{
		this.initServices();

		let feature = CAPFeaturePolicyTestRequest;

		return this.authService.getPolicyForFeature(feature).then( r => {
			console.log("This is the CAP policy reply", r);
			let resp = r;
			if ( resp && resp.results )
			{
                const capResponse = resp.results.find(res => {
                    return res.resource === EnableCAPAccountAccessStatement.resource[0];
                });
                if (capResponse && PolicyEffects[capResponse.effect] === PolicyEffects.ExplicitAllow) 
				{
					return true;
				}
				else 
					return false;
			}
			else
			{
				return false;
			}

		}).catch(err => {
			console.log("Failed to test the CAP policy, returning false", err);
			return false;
		});
	}

	removeCAPFeature( account: Account )
	{
		this.initServices();
		return this.policyService.removeAccountPolicyIfItExists(account, EnableCAPAccountAccessStatement).then( ret => {
			return ret;
		}).catch(err => {
			console.log("Failed to remove policy", err);
			throw err;
		});
	}

	removeDeleteDisassociatePolicy( account: Account ): Promise<Account>
	{
		let dataChanges: any = {
			policy: {
				behaviorOfIntegrationDelete: "delete"
			}
		};
		
		return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
			return result;
		});
	}

}
